import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthService } from '../http/auth.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { ToastrService } from 'ngx-toastr';

import { TranslatePipe } from '@app/pipes/translate.pipe';
import { environment } from '../../../environments/environment';
import { UserService } from '../http/user.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
  httpClient: HttpClient;
  // URL Query Params
  adClickIds: string[] = [
    'gclid',
    'wbraid',
    'gbraid',
    'msclkid',
    'fbclid',
    'SsCid',
    'TTCLID',
    'twclid',
  ];
  // Boolean flag to prevent sending multiple requests in parallel with the same data
  sendingAdsData = false;
  constructor(
    public auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private translatePipe: TranslatePipe,
    private toastr: ToastrService,
    private handler: HttpBackend,
    private stateService: StateService,
    private storage: StorageService,
    private userService: UserService,
  ) {
    this.httpClient = new HttpClient(handler);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    try {
      // TEMP ENSURE WE LOG OUT FOR INFINTIE LOOP FOR FALLBACK COOKIE
      this.storage.setCookie('pinploy_authToken', '', -1);
    } catch (err) {
      console.log(err);
    }
    request = request.clone({ withCredentials: true });
    // Get auth token from url params
    this.route.queryParams.subscribe((params) => {
      if (params?.token && !this.storage?.authToken) {
        request = request.clone({
          setHeaders: { Authorization: params.token },
        });
        this.storage.authToken = params.token;
        this.auth.authStatusSource.next(true);
      }
    });
    if (request.url.indexOf('stripe.com/v3') !== -1) {
      // Include Stripe Secret Key as Authorization
      const stripeKey = environment.stripeLive
        ? environment.stripeKeyLive
        : environment.stripeKeyTest;
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${stripeKey}`,
        },
      });
    } else if (request.url.indexOf(environment.apiUrl) !== -1) {
      const clone = {
        setHeaders: {
          'x-front-end-route': this.router.url,
        },
      };
      const token = this.auth.getToken();
      if (token) {
        clone.setHeaders['Authorization'] = token;
      }
      if (this.storage.socketId) {
        clone.setHeaders['Socket'] = this.storage.socketId;
      }
      clone.setHeaders['X-Handyhand-Client'] = 'web';
      request = request.clone(clone);
      this.route.queryParams.subscribe((params: Params) => {
        // ServerSide Tracking set AdClickIds to storage
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            this.storage.storeAdsClick(key, value);
          });
        }
      });
      const adsClickData = this.storage.adsClickObj;
      if (token && adsClickData && !this.sendingAdsData) {
        this.sendingAdsData = true;
        this.userService.saveAdsClick(adsClickData).subscribe({
          next: () => {
            this.storage.adsClickObj = null;
          },
          error: (err) => {
            console.log(err);
          },
          complete: () => {
            this.sendingAdsData = false;
          },
        });
      }
    }

    return next.handle(request).pipe(
      tap({
        error: (err) => {
          // Unauthorized
          if (err.status === 401) {
            this.toastr.info(
              this.translatePipe.transform('TOAST_MESSAGES.INFO.UPDATE'),
            );
            return this.auth.logout();
          }
          this.stateService.toggleWaitingPage(false);
          if (err.status === 301) {
            return this.toastr.success(
              this.translatePipe.transform('TOAST_MESSAGES.SUCCESS.REDIRECT'),
            );
          }
          if (!err.error) {
            this.toastr.error(
              this.translatePipe.transform('TOAST_MESSAGES.ERROR.GENERIC_ERR'),
            );
          } else if (err.error === 'Token does not exist') {
            if (request.url.indexOf('/reset-password') !== -1) {
              this.toastr.warning(
                this.translatePipe.transform(
                  'TOAST_MESSAGES.WARNING.SESS_EXPIRE',
                ),
              );
            }
          } else if (typeof err.error === 'object') {
            this.toastr.error(
              this.translatePipe.transform('TOAST_MESSAGES.ERROR.GENERIC_ERR'),
            );
          } else {
            this.toastr.error(err.error);
          }
        },
      }),
    );
  }
}
